import React, { useState, useEffect } from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatAmount, formatNumber } from "@/utils/formatters";
import { cn } from "@/lib/utils";

interface MarginCalculatorProps {
  productPrice?: number;
  initialAffiliateCommission?: number;
  onCalculationChange?: (calculations: MarginCalculations) => void;
  /** Rendered inside a sheet or dialog: no card chrome, no heading. */
  embedded?: boolean;
}

interface MarginCalculations {
  productPrice: number;
  affiliateCommission: number;
  netRevenue: number;
  profitMargin: number;
  roi: number;
  breakEvenSales: number;
  targetSales: number;
  targetRevenue: number;
}

type ProgramType = "affiliate" | "campaign";

interface ProgramConfig {
  type: ProgramType;
  affiliatePercentage?: number;
  affiliateDiscountPercentage?: number;
  discountPercentage?: number;
  campaignCommission?: number;
}

/** A field with a unit sitting inside the input's left edge. */
function UnitField({
  id,
  label,
  unit,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  label: string;
  unit: "$" | "%";
  value: string | number;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-foreground">
        {label}
      </Label>
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">
          {unit}
        </span>
        <Input
          id={id}
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="pl-7 tabular-nums"
        />
      </div>
    </div>
  );
}

function ResultRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "negative";
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-t border-border py-2.5 first:border-t-0">
      <span className="text-[13px] font-medium text-foreground">{label}</span>
      <span
        className={cn(
          "text-lg font-semibold tabular-nums tracking-tight",
          tone === "negative" ? "text-destructive" : "text-foreground"
        )}
      >
        {value}
      </span>
    </div>
  );
}

export const MarginCalculator: React.FC<MarginCalculatorProps> = ({
  productPrice = 0,
  initialAffiliateCommission = 0,
  onCalculationChange,
  embedded = false,
}) => {
  const [calculations, setCalculations] = useState<MarginCalculations>({
    productPrice: productPrice,
    affiliateCommission: 0,
    netRevenue: 0,
    profitMargin: 0,
    roi: 0,
    breakEvenSales: 0,
    targetSales: 0,
    targetRevenue: 0,
  });

  const [targetProfit, setTargetProfit] = useState("");
  const [costOfGoods, setCostOfGoods] = useState("");
  const [programType, setProgramType] = useState<ProgramType>("affiliate");
  const [programConfig, setProgramConfig] = useState<ProgramConfig>({
    type: "affiliate",
    affiliatePercentage: initialAffiliateCommission,
  });

  useEffect(() => {
    setCalculations((prev) => ({ ...prev, productPrice }));
  }, [productPrice]);

  // Update programConfig when initialAffiliateCommission changes
  useEffect(() => {
    setProgramConfig((prev) => ({
      ...prev,
      affiliatePercentage: initialAffiliateCommission,
    }));
  }, [initialAffiliateCommission]);

  // Validation functions
  const validateNumberInput = (value: string): boolean => {
    const regex = /^(?!0+$)\d+(\.\d{1,2})?$/;
    return regex.test(value) || value === "";
  };

  const validatePercentageInput = (value: string): boolean => {
    const regex = /^(?!0+$)([0-9]|[1-9][0-9]|100)(\.\d{1,2})?$/;
    return regex.test(value) || value === "";
  };

  // Calculate margins and ROI in real-time
  useEffect(() => {
    const productPriceNum = Number(calculations.productPrice) || 0;
    const costOfGoodsNum = Number(costOfGoods) || 0;
    const targetProfitNum = Number(targetProfit) || 0;

    // Calculate affiliate commission based on program type
    let affiliateCommission = 0;
    let netRevenue = productPriceNum;

    if (programType === "affiliate") {
      const affiliatePercentage = programConfig.affiliatePercentage || 0;
      const affiliateDiscountPercentage =
        programConfig.affiliateDiscountPercentage || 0;

      // Apply discount first if present
      const discountedPrice =
        affiliateDiscountPercentage > 0
          ? productPriceNum -
            (productPriceNum * affiliateDiscountPercentage) / 100
          : productPriceNum;

      // Then apply affiliate commission on the discounted price
      affiliateCommission = (discountedPrice * affiliatePercentage) / 100;
      netRevenue = discountedPrice - affiliateCommission;
    } else if (programType === "campaign") {
      const discountPercentage = programConfig.discountPercentage || 0;
      const campaignCommission = programConfig.campaignCommission || 0;

      // Apply discount first
      const discountedPrice =
        productPriceNum - (productPriceNum * discountPercentage) / 100;

      // Then apply campaign commission
      affiliateCommission = (discountedPrice * campaignCommission) / 100;
      netRevenue = discountedPrice - affiliateCommission;
    }

    // Calculate profit per unit (revenue - cost of goods)
    const profitPerUnit = netRevenue - costOfGoodsNum;

    // Calculate profit margin percentage (profit per unit / net revenue)
    const profitMargin =
      netRevenue > 0 ? (profitPerUnit / netRevenue) * 100 : 0;

    // Calculate ROI (profit per unit / cost of goods)
    const roi = costOfGoodsNum > 0 ? (profitPerUnit / costOfGoodsNum) * 100 : 0;

    // Calculate break-even sales (when profit = 0)
    const breakEvenSales =
      profitPerUnit > 0 ? Math.ceil(costOfGoodsNum / profitPerUnit) : 0;

    // Calculate target sales for desired profit
    const targetSales =
      profitPerUnit > 0 && targetProfitNum > 0
        ? Math.ceil(targetProfitNum / profitPerUnit)
        : 0;

    // Calculate target revenue (target sales * net revenue per unit)
    const targetRevenue = targetSales * netRevenue;

    const newCalculations: MarginCalculations = {
      productPrice: productPriceNum,
      affiliateCommission,
      netRevenue,
      profitMargin,
      roi,
      breakEvenSales,
      targetSales,
      targetRevenue,
    };

    setCalculations(newCalculations);
    onCalculationChange?.(newCalculations);
  }, [
    calculations.productPrice,
    costOfGoods,
    targetProfit,
    programType,
    programConfig,
    onCalculationChange,
  ]);

  const handleInputChange = (
    field: keyof MarginCalculations,
    value: string
  ) => {
    if (validateNumberInput(value)) {
      const numValue = parseFloat(value) || 0;
      setCalculations((prev) => ({
        ...prev,
        [field]: numValue,
      }));
    }
  };

  const handleNumberInput = (
    value: string,
    setter: (value: string) => void
  ) => {
    if (validateNumberInput(value)) {
      setter(value);
    }
  };

  const handlePercentageInput = (
    value: string,
    setter: (value: number) => void
  ) => {
    if (validatePercentageInput(value)) {
      setter(parseFloat(value) || 0);
    }
  };

  const handleProgramTypeChange = (type: ProgramType) => {
    setProgramType(type);
    if (type === "affiliate") {
      setProgramConfig({
        type: "affiliate",
        affiliatePercentage: 0,
        affiliateDiscountPercentage: 0,
      });
    } else {
      setProgramConfig({
        type: "campaign",
        discountPercentage: 0,
        campaignCommission: 0,
      });
    }
  };

  const formatPercentage = (value: number) => `${value.toFixed(1)}%`;
  const profitPerUnit = calculations.netRevenue - (Number(costOfGoods) || 0);

  return (
    <div
      className={cn(
        !embedded && "rounded-lg border border-border bg-card",
        embedded ? "space-y-6" : "p-5 space-y-6"
      )}
    >
      {!embedded && (
        <h3 className="text-[15px] font-semibold text-foreground">Margin calculator</h3>
      )}

      <div className={cn("grid gap-6", !embedded && "lg:grid-cols-2")}>
        <div className="space-y-4">
          <UnitField
            id="mc-price"
            label="Product price"
            unit="$"
            value={calculations.productPrice || ""}
            onChange={(v) => handleInputChange("productPrice", v)}
            placeholder="0.00"
          />
          <UnitField
            id="mc-cogs"
            label="Cost of goods"
            unit="$"
            value={costOfGoods}
            onChange={(v) => handleNumberInput(v, setCostOfGoods)}
            placeholder="0.00"
          />

          <div className="space-y-1.5">
            <Label htmlFor="mc-type" className="text-foreground">
              Sold through
            </Label>
            <Select
              value={programType}
              onValueChange={(v) => handleProgramTypeChange(v as ProgramType)}
            >
              <SelectTrigger id="mc-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="affiliate">Affiliate program</SelectItem>
                <SelectItem value="campaign">Campaign</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {programType === "affiliate" ? (
            <>
              <UnitField
                id="mc-aff-discount"
                label="Customer discount"
                unit="%"
                value={programConfig.affiliateDiscountPercentage || ""}
                onChange={(v) =>
                  handlePercentageInput(v, (value) =>
                    setProgramConfig((prev) => ({ ...prev, affiliateDiscountPercentage: value }))
                  )
                }
                placeholder="0"
              />
              <UnitField
                id="mc-aff-commission"
                label="Affiliate commission"
                unit="%"
                value={programConfig.affiliatePercentage || ""}
                onChange={(v) =>
                  handlePercentageInput(v, (value) =>
                    setProgramConfig((prev) => ({ ...prev, affiliatePercentage: value }))
                  )
                }
                placeholder="0"
              />
            </>
          ) : (
            <>
              <UnitField
                id="mc-camp-discount"
                label="Campaign discount"
                unit="%"
                value={programConfig.discountPercentage || ""}
                onChange={(v) =>
                  handlePercentageInput(v, (value) =>
                    setProgramConfig((prev) => ({ ...prev, discountPercentage: value }))
                  )
                }
                placeholder="0"
              />
              <UnitField
                id="mc-camp-commission"
                label="Affiliate commission"
                unit="%"
                value={programConfig.campaignCommission || ""}
                onChange={(v) =>
                  handlePercentageInput(v, (value) =>
                    setProgramConfig((prev) => ({ ...prev, campaignCommission: value }))
                  )
                }
                placeholder="0"
              />
            </>
          )}

          <UnitField
            id="mc-target"
            label="Target profit"
            unit="$"
            value={targetProfit}
            onChange={(v) => handleNumberInput(v, setTargetProfit)}
            placeholder="0.00"
          />
        </div>

        <div>
          <ResultRow label="Affiliate commission" value={formatAmount(calculations.affiliateCommission)} />
          <ResultRow label="Net revenue" value={formatAmount(calculations.netRevenue)} />
          <ResultRow
            label="Profit per unit"
            value={formatAmount(profitPerUnit)}
            tone={profitPerUnit < 0 ? "negative" : undefined}
          />
          <ResultRow
            label="Profit margin"
            value={formatPercentage(calculations.profitMargin)}
            tone={calculations.profitMargin < 0 ? "negative" : undefined}
          />
          <ResultRow
            label="Return on cost"
            value={formatPercentage(calculations.roi)}
            tone={calculations.roi < 0 ? "negative" : undefined}
          />
          <ResultRow label="Break-even sales" value={`${formatNumber(calculations.breakEvenSales)} units`} />
          <ResultRow label="Sales for target" value={`${formatNumber(calculations.targetSales)} units`} />
          <ResultRow label="Revenue at target" value={formatAmount(calculations.targetRevenue)} />
        </div>
      </div>
    </div>
  );
};
